import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ModeProvider } from './contexts/ModeContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import FindUserIdPage from './pages/FindUserIdPage';
import SocialCallbackPage from './pages/SocialCallbackPage';
import EnhancedProfilePage from './pages/EnhancedProfilePage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectCreatePage from './pages/ProjectCreatePage';
import DashboardPage from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ProjectsPage from './pages/ProjectsPage';
import ChatPage from './pages/Chat/ChatPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import ServiceCreatePage from './pages/ServiceCreatePage';
import MyProjectsPage from './pages/MyProjectsPage';
import MyServicesPage from './pages/MyServicesPage';
import ProjectEditPage from './pages/ProjectEditPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ServiceEditPage from './pages/ServiceEditPage';
import TransactionsPage from './pages/TransactionsPage';
import ReportPage from './pages/ReportPage';
import FeeCalculatorPage from './pages/FeeCalculatorPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import OrdersPage from './pages/OrdersPage';
import ExpertVerificationPage from './pages/ExpertVerificationPage';
import AdminSettlementPage from './pages/AdminSettlementPage';
import NotificationsPage from './pages/NotificationsPage';
import FreelancerProfilePage from './pages/FreelancerProfilePage';
import AICreditsPage from './pages/AICreditsPage';
import AIContractDraftPage from './pages/AIContractDraftPage';
import NavBar from './components/NavBar';
import PrivateRoute from './components/PrivateRoute';
import { initNativeAppListeners } from './utils/appNative';

// NavBar를 조건부로 렌더링하는 컴포넌트
const AppContent = () => {
  const location = useLocation();
  
  // NavBar를 표시하지 않을 페이지들
  // /login, /register: 자체 풀스크린 레이아웃 + 하단에 로그인/가입 전환 링크가 이미 있어
  // 상단 NavBar의 "로그인"/"시작하기" 버튼과 중복 노출되어 "눌러도 차이가 없다"는 혼란을 유발했음
  const hideNavBarPaths = ['/', '/privacy', '/terms', '/login', '/register'];
  const shouldShowNavBar = !hideNavBarPaths.includes(location.pathname);

  return (
    <>
      {shouldShowNavBar && <NavBar />}
      <Routes>
        {/* 공개 페이지 */}
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:id" element={<ServiceDetailPage />} />
        <Route path="/freelancer/:nickname" element={<FreelancerProfilePage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/find-userid" element={<FindUserIdPage />} />
        <Route path="/auth/callback" element={<SocialCallbackPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        
        {/* 인증이 필요한 페이지 */}
        <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><EnhancedProfilePage /></PrivateRoute>} />
        <Route path="/delete-account" element={<PrivateRoute><DeleteAccountPage /></PrivateRoute>} />
        <Route path="/project-create" element={<PrivateRoute><ProjectCreatePage /></PrivateRoute>} />
        <Route path="/project-edit/:id" element={<PrivateRoute><ProjectEditPage /></PrivateRoute>} />
        <Route path="/chat" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
        <Route path="/chat/:roomId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
        <Route path="/service-create" element={<PrivateRoute><ServiceCreatePage /></PrivateRoute>} />
        <Route path="/service-edit/:id" element={<PrivateRoute><ServiceEditPage /></PrivateRoute>} />
        <Route path="/my-projects" element={<PrivateRoute><MyProjectsPage /></PrivateRoute>} />
        <Route path="/my-services" element={<PrivateRoute><MyServicesPage /></PrivateRoute>} />
        
        {/* 거래 관련 페이지들 */}
        <Route path="/transactions" element={<PrivateRoute><TransactionsPage /></PrivateRoute>} />
        <Route path="/orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />

        {/* AI 리포트 */}
        <Route path="/report/:userId" element={<PrivateRoute><ReportPage /></PrivateRoute>} />
        {/* V4.0: 3대 AI 기능 통합 이용권 구매(웹 전용) + AI 계약서 초안 */}
        <Route path="/ai-credits/:toolType" element={<PrivateRoute><AICreditsPage /></PrivateRoute>} />
        <Route path="/contract-ai/:roomId" element={<PrivateRoute><AIContractDraftPage /></PrivateRoute>} />

        {/* 수수료 계산기 */}
        <Route path="/fee-calculator" element={<FeeCalculatorPage />} />
        
        {/* 전문가 인증 */}
        <Route path="/expert-verification" element={<PrivateRoute><ExpertVerificationPage /></PrivateRoute>} />

        {/* 알림 */}
        <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />

        {/* 관리자 페이지 */}
        <Route path="/admin" element={<PrivateRoute><AdminSettlementPage /></PrivateRoute>} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App = () => {
  // 네이티브(앱)일 때만 동작: 뒤로가기 버튼 + 소셜 로그인 딥링크 복귀
  React.useEffect(() => { initNativeAppListeners(); }, []);
  return (
    <AuthProvider>
      <ModeProvider>
        <Router>
          <AppContent />
        </Router>
      </ModeProvider>
    </AuthProvider>
  );
};

export default App;
